import { useEffect, useMemo, useState } from "react";
import { FiArrowLeft, FiArrowRight, FiX } from "react-icons/fi";

import { Button } from "../Buttons";
import { Modal } from "../Modal";
import AIChatStep from "./steps/AIChatStep";
import FinalConsentStep from "./steps/FinalConsentStep";
import IntakeReportsStep from "./steps/IntakeReportsStep";
import IntroStep from "./steps/IntroStep";
import SessionNotesStep from "./steps/SessionNotesStep";
import { CloseIcon, Content, Dot, Footer, Header, StepDots } from "./styles";
import { STEP_HEADER_CONFIG } from "./constants";
import { DAVYS_GREY_800, MOSS_GREEN_800 } from "../colors";
import FlexBox from "../FlexBox";
import { StringParam, useQueryParams } from "use-query-params";

const AIIntroModal = ({
  onClose,
  onSave,
  isEnabled,
  setIsEnabled,
  stepFlags = {},
}) => {
  const [stepIndex, setStepIndex] = useState(0);
  const [queryParams] = useQueryParams({
    aiConsentSource: StringParam,
  });
  const { aiConsentSource } = queryParams || {};

  const visibleSteps = useMemo(
    () => [
      {
        key: "intro",
        render: () => <IntroStep />,
      },
      ...(stepFlags.sessionNotes
        ? [
            {
              key: "session-notes",
              render: () => <SessionNotesStep />,
            },
          ]
        : []),
      ...(stepFlags.intakeReports
        ? [
            {
              key: "intake-reports",
              render: () => <IntakeReportsStep />,
            },
          ]
        : []),
      ...(stepFlags.aiChat
        ? [
            {
              key: "ai-chat",
              render: () => <AIChatStep />,
            },
          ]
        : []),
      {
        key: "final-consent",
        render: () => (
          <FinalConsentStep
            isEnabled={isEnabled}
            onToggle={() => setIsEnabled(prev => !prev)}
          />
        ),
      },
    ],
    [
      isEnabled,
      setIsEnabled,
      stepFlags.aiChat,
      stepFlags.intakeReports,
      stepFlags.sessionNotes,
    ]
  );

  useEffect(() => {
    if (stepIndex > visibleSteps.length - 1) {
      setStepIndex(Math.max(visibleSteps.length - 1, 0));
    }
  }, [stepIndex, visibleSteps.length]);

  const isFirst = stepIndex === 0;
  const isLast = stepIndex === visibleSteps.length - 1;

  const next = () =>
    setStepIndex(s => Math.min(s + 1, visibleSteps.length - 1));
  const back = () => setStepIndex(s => Math.max(s - 1, 0));

  const currentStep = visibleSteps[stepIndex];
  const { bgImage, headerHeight, bgSize } =
    STEP_HEADER_CONFIG[currentStep?.key] || {};

  return (
    <Modal
      M1
      height="auto"
      togglePopup={onClose}
      backgroundClickEnabled
      borderRadius="1rem"
      maxHeight="90vh"
    >
      <Header bgImage={bgImage} headerHeight={headerHeight} bgSize={bgSize}>
        <CloseIcon onClick={onClose}>
          <FiX />
        </CloseIcon>
      </Header>

      <Content>{currentStep?.render?.()}</Content>

      <Footer>
        {!isFirst ? (
          <Button
            color={DAVYS_GREY_800}
            onClick={back}
            hoverColor={DAVYS_GREY_800}
            outline
            tertiary
            width="10rem"
          >
            <FlexBox justify="center" align="center" columnGap="0.5rem">
              <FiArrowLeft color={MOSS_GREEN_800} size={20} /> PREVIOUS
            </FlexBox>
          </Button>
        ) : (
          <span />
        )}

        <StepDots>
          {visibleSteps.map((step, i) => (
            <Dot key={step.key} active={i === stepIndex} />
          ))}
        </StepDots>

        {isLast ? (
          <Button
            width={aiConsentSource !== "aiHome" ? "12rem" : "10rem"}
            primary
            onClick={onSave}
          >
            {aiConsentSource === "aiHome" ? (
              "Done"
            ) : (
              <FlexBox justify="center" align="center" columnGap="0.5rem">
                GET STARTED <FiArrowRight size={20} />
              </FlexBox>
            )}
          </Button>
        ) : (
          <Button width="10rem" primary onClick={next}>
            <FlexBox justify="center" align="center" columnGap="0.25rem">
              <FlexBox width="5.25rem" justify="center" align="center">
                NEXT
              </FlexBox>
              <FiArrowRight size={20} />
            </FlexBox>
          </Button>
        )}
      </Footer>
    </Modal>
  );
};

export default AIIntroModal;
